import gql from "graphql-tag";
import * as Cookies from "es-cookie";

import client from "../apolloClient";

/**
 * Shared documents for the `studentGlobals` field.
 *
 * 63 live files select `studentGlobals`, in 22 different shapes. They refetch on
 * every page mount, and — unlike the other fields cleaned up in this pass — the
 * cause is not the fetch policy: 20 of the registrations are already the default
 * `cache-first`. The cause is that no two documents agree on a selection set, so
 * `cache.diff` is never `complete` and Apollo goes to the network every time.
 *
 * The fix is therefore to collapse those selection sets into the documents
 * below, not to change any policy. They all target the same root field with the
 * same argument, so they share one `ROOT_QUERY.studentGlobals({...})` record and
 * merge into it field by field.
 *
 * A localStorage-backed store (the shape used for notifications) is the wrong
 * tool here: 20 call sites read and patch this data through
 * `store.readQuery`/`writeQuery` inside mutation `update` callbacks, and every
 * consumer depends on the HOC's `loading`/`error` contract. Both are
 * Apollo-shaped.
 */

/**
 * Everything the pages sharing this document actually read. Used with
 * `cache-first`, so it is fetched once and served from the cache thereafter.
 *
 * THE INVARIANT, which a first attempt got wrong: this is the union of what its
 * OWN CONSUMERS select — not the union of every `studentGlobals` document in the
 * tree. That first version was built across all 63 files, but only 22 were
 * migrated onto it, so it carried `loginRedirectionPath`, `uid`, `previousYears`,
 * `appModules`, `state` and `exams.exam_subjects` for zero consumers.
 *
 * That is not merely wasteful. GraphQL fails the whole response if any selected
 * field's resolver throws, and every consumer here reacts to `error` with an
 * alert and a blank render — so an unused field can take out all 22 pages.
 * `exams.exam_subjects` did exactly that: `Exam.exam_subjects` runs
 * `JSON.parse(parent.attributes)` unguarded, and `StudentGlobals.exams` returns a
 * hard-coded list of 9-10 exams rather than the student's own, so one exam row
 * with a null or malformed `attributes` column broke every page. Keep this
 * document to what its consumers dereference.
 *
 * Two fields on the type are excluded on purpose, and both carve-outs are
 * load-bearing:
 *   - `tags { count }` — one COUNT(*) per tag per field, and it changes on every
 *     bookmark. The pages that display counts keep their own document on
 *     `cache-and-network`. It must also stay out because the mutation callbacks
 *     push a new tag as {id, tag, type} with no `count`; a cache-first watcher
 *     selecting `count` would then read incomplete and refetch in a loop.
 *   - `previousSets` — carries `attempted` / `is_started` / `examsessionid`, i.e.
 *     live exam state. The previous-papers pages keep `network-only`.
 */
export const STUDENT_GLOBALS = gql`
  query($student_user_exam_id: Int!) {
    studentGlobals(student_user_exam_id: $student_user_exam_id) {
      exam_time
      exams {
        id
        exam
      }
      classes {
        id
        class
      }
      questionTypes {
        id
        questiontype
      }
      complexity {
        id
        complexity
      }
      questionTheory {
        id
        question_theory
      }
      reports {
        id
        report
      }
      errorReasons {
        id
        reason
      }
      contentTypes {
        id
        customcontent
      }
      examInstructions {
        is_exam
        instructions
      }
      tags {
        id
        tag
        type
      }
    }
  }
`;

/**
 * The minimum a mutation needs in order to push a newly created tag into the
 * cache.
 *
 * `store.readQuery` is not the forgiving path: on a partial match it throws
 * `InvariantError: Can't find field ...` rather than returning what it has. The
 * 20 update callbacks each declared their own private `FETCH_GLOBALS`, and at
 * least one of them asked for more than its own page had fetched — see the note
 * in the migration for `ErrorExamSectionMiddle`. Reading through the narrowest
 * possible document removes that whole class of failure.
 */
export const STUDENT_GLOBALS_TAGS = gql`
  query($student_user_exam_id: Int!) {
    studentGlobals(student_user_exam_id: $student_user_exam_id) {
      tags {
        id
        tag
        type
      }
    }
  }
`;

// No document here for `tags { count }` or for `previousSets`: the pages that
// need them keep their own, on the network policies their volatility requires.
// See the carve-out note on STUDENT_GLOBALS for why they must not be folded in.

function currentStudentExamId() {
  const id = parseInt(Cookies.get("student_user_exam_id"), 10);
  return isNaN(id) ? null : id;
}

/**
 * Fetch the reference set if the cache does not already hold it.
 *
 * `cache-first` makes this idempotent, so it is safe to call from every login
 * path as well as from the post-login loading screen. Priming here means the
 * first route the student lands on reads from a warm cache instead of issuing
 * its own request.
 *
 * @returns {Promise} never rejects.
 */
export function primeStudentGlobals() {
  const studentUserExamId = currentStudentExamId();
  if (studentUserExamId === null || !Cookies.get("studenttoken")) {
    return Promise.resolve(null);
  }

  return client
    .query({
      query: STUDENT_GLOBALS,
      variables: { student_user_exam_id: studentUserExamId },
      fetchPolicy: "cache-first"
    })
    .then(result => (result.data && result.data.studentGlobals) || null)
    .catch(error => {
      // Swallowed: the pages that need this run their own HOC and surface their
      // own loading and error states. Priming is an optimisation, and failing it
      // must not block a login.
      console.error("studentGlobals prime failed", error);
      return null;
    });
}

/**
 * Synchronous read of the primed reference set, for code outside a component.
 * Returns null rather than throwing when the cache has not been filled yet —
 * `readQuery` raises an invariant on a miss.
 */
export function getStudentGlobals() {
  const studentUserExamId = currentStudentExamId();
  if (studentUserExamId === null) return null;
  try {
    const data = client.readQuery({
      query: STUDENT_GLOBALS,
      variables: { student_user_exam_id: studentUserExamId }
    });
    return (data && data.studentGlobals) || null;
  } catch (e) {
    return null;
  }
}

/**
 * Drop the cached globals. Logout navigates with `history.push`, so the
 * InMemoryCache survives it; entries are keyed by student_user_exam_id and so do
 * not collide, but the previous student's data would otherwise linger in memory.
 */
export function resetStudentGlobals() {
  const studentUserExamId = currentStudentExamId();
  if (studentUserExamId === null) return;
  try {
    client.writeQuery({
      query: STUDENT_GLOBALS,
      variables: { student_user_exam_id: studentUserExamId },
      data: { studentGlobals: null }
    });
  } catch (e) {
    // Nothing cached to clear.
  }
}

export default primeStudentGlobals;
